/**
 * AdvancedFilterModal.js
 *
 * LGX-653: generic nested AND/OR filter builder, adapted from Shipment's
 * AdvancedFilterModal (TabContent/shipmentShared.js) to take a `ruleFields` prop instead of a
 * hardcoded field catalog, so any grid can reuse it.
 */
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { SlidersHorizontal, X, RotateCcw, Plus, Trash2 } from "lucide-react";
import { FaRegCalendarAlt } from "react-icons/fa";
import {
  MAX_RULE_DEPTH,
  ruleFieldByKey,
  ruleFieldGroups,
  operatorLabel,
  makeRule,
  makeRuleGroup,
  makeRuleId,
  isRuleComplete,
  isRuleGroup,
  ruleMatchType,
  pruneRuleNodes,
  countRuleNodes,
  updateRuleNode,
  removeRuleNode,
  appendRuleNode,
} from "./filterTree";

const RULE_CONTROL_BASE =
  "w-full text-sm rounded-lg border border-gray-300 dark:border-acs-dark-border " +
  "bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text " +
  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const RULE_CONTROL_CLASS = `${RULE_CONTROL_BASE} px-2.5 py-1.5`;
const RULE_SELECT_CLASS = `${RULE_CONTROL_BASE} pl-2.5 pr-8 py-1.5 truncate`;
const RULE_CONJUNCTION_CLASS = `${RULE_CONTROL_BASE} pl-2 pr-6 py-1.5 text-[11px] font-semibold uppercase tracking-wide`;

const coerceValueForOp = (value, nextOp) => {
  if (nextOp === "IN") {
    if (Array.isArray(value)) return value;
    const single = String(value ?? "").trim();
    return single ? [single] : [];
  }
  if (Array.isArray(value)) return value[0] ?? "";
  return value ?? "";
};

const DatePickerButton = ({ value, onChange }) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const formatted = value ? (([y, m, d]) => `${m} / ${d} / ${y}`)(value.split("-")) : "";

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className="relative flex items-center w-full px-3 py-1.5 text-sm rounded-lg border
        overflow-hidden border-gray-300 dark:border-acs-dark-border bg-white
        dark:bg-acs-dark-surface focus-within:ring-2 focus-within:ring-blue-500
        focus-within:border-blue-500 transition-colors cursor-pointer"
    >
      <span
        className={`flex-1 ${formatted ? "text-gray-900 dark:text-acs-dark-text" : "text-gray-400 dark:placeholder-gray-500"} ${focused ? "invisible" : ""}`}
      >
        {formatted || "mm / dd / yyyy"}
      </span>
      <FaRegCalendarAlt
        size={14}
        onClick={(e) => { e.stopPropagation(); inputRef.current?.showPicker(); }}
        className={`text-custom-bg-blue dark:text-acs-accent flex-shrink-0 cursor-pointer ${focused ? "invisible" : ""}`}
      />
      <input
        ref={inputRef}
        type="date"
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 opacity-0 cursor-pointer"
      />
    </div>
  );
};

const RuleValueEditor = ({ rule, field, onChange }) => {
  if (field.type === "date") return <DatePickerButton value={rule.value || ""} onChange={onChange} />;

  if (field.type === "number")
    return (
      <input
        type="number"
        value={Array.isArray(rule.value) ? rule.value[0] ?? "" : rule.value ?? ""}
        placeholder={field.placeholder || "Enter a number"}
        onChange={(e) => onChange(e.target.value)}
        className={`${RULE_CONTROL_CLASS} placeholder-gray-400 dark:placeholder-gray-500`}
      />
    );

  if (field.type === "select") {
    if (rule.op === "IN") {
      const selected = Array.isArray(rule.value) ? rule.value : [];
      return (
        <div className="flex flex-wrap gap-1.5 py-0.5">
          {field.options.map((o) => {
            const on = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() => onChange(on ? selected.filter((v) => v !== o) : [...selected, o])}
                className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-all ${
                  on
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "bg-transparent border-gray-300 dark:border-acs-dark-border text-gray-600 dark:text-acs-dark-text hover:border-blue-400 hover:text-blue-600"
                }`}
              >
                {o}
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <select
        value={Array.isArray(rule.value) ? rule.value[0] ?? "" : rule.value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={RULE_SELECT_CLASS}
      >
        <option value="">Select a value…</option>
        {field.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      type="text"
      value={Array.isArray(rule.value) ? rule.value.join(", ") : rule.value || ""}
      placeholder={field.placeholder || "Enter a value"}
      onChange={(e) => onChange(e.target.value)}
      className={`${RULE_CONTROL_CLASS} placeholder-gray-400 dark:placeholder-gray-500`}
    />
  );
};

const AddNodeButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-dashed border-gray-300 dark:border-acs-dark-border text-gray-600 dark:text-acs-dark-text hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
  >
    <Plus size={13} /> {children}
  </button>
);

const RuleLeafRow = ({ ruleFields, rule, actions }) => {
  const field = ruleFieldByKey(ruleFields, rule.field) ?? ruleFields[0];
  const incomplete = !isRuleComplete(ruleFields, rule);
  const groups = ruleFieldGroups(ruleFields);

  return (
    <div
      className={`grid grid-cols-[minmax(0,1.3fr)_minmax(0,0.9fr)_minmax(0,1.25fr)] items-start gap-2 rounded-lg border px-2 py-2 ${
        incomplete
          ? "border-acs-secondary-purple/60 dark:border-acs-secondary-purple/50 bg-custom-bg-light-purple/50 dark:bg-acs-secondary-purple/10"
          : "border-gray-200 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface"
      }`}
    >
      <select
        value={rule.field}
        onChange={(e) => actions.changeField(rule, e.target.value)}
        title={field.label}
        className={RULE_SELECT_CLASS}
      >
        {groups.map((group) => (
          <optgroup key={group.name} label={group.name}>
            {group.fields.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </optgroup>
        ))}
      </select>

      <select
        value={rule.op}
        onChange={(e) => actions.changeOp(rule, e.target.value)}
        disabled={field.ops.length === 1}
        className={`${RULE_SELECT_CLASS} ${field.ops.length === 1 ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        {field.ops.map((op) => (
          <option key={op} value={op}>{operatorLabel(op)}</option>
        ))}
      </select>

      <RuleValueEditor rule={rule} field={field} onChange={(v) => actions.changeValue(rule.id, v)} />
    </div>
  );
};

const RuleGroupBox = ({ ruleFields, group, depth, actions }) => {
  const groupMatch = ruleMatchType(group);

  return (
    <div className="rounded-lg border border-gray-300 dark:border-acs-dark-border bg-gray-50 dark:bg-white/[0.03] px-2 py-2">
      <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Match {groupMatch === "OR" ? "any" : "all"} of
      </p>

      {group.children.length === 0 ? (
        <p className="px-1 pb-1 text-xs text-gray-400 dark:text-gray-500">
          Empty group — add a condition or it will be dropped.
        </p>
      ) : (
        <RuleNodeList
          ruleFields={ruleFields}
          nodes={group.children}
          matchType={groupMatch}
          groupId={group.id}
          depth={depth + 1}
          actions={actions}
        />
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        <AddNodeButton onClick={() => actions.addRule(group.id)}>Add condition</AddNodeButton>
        {depth + 1 < MAX_RULE_DEPTH && (
          <AddNodeButton onClick={() => actions.addGroup(group.id, groupMatch)}>Add group</AddNodeButton>
        )}
      </div>
    </div>
  );
};

const RuleNodeList = ({ ruleFields, nodes, matchType, groupId, depth, actions }) => (
  <div className="space-y-2">
    {nodes.map((node, idx) => {
      const isGroup = isRuleGroup(node);
      return (
        <div key={node.id} className="flex items-start gap-2">
          <div className="w-[74px] flex-shrink-0">
            {idx === 1 ? (
              <select
                value={matchType}
                onChange={(e) => actions.setMatchType(groupId, e.target.value)}
                aria-label="Combine these conditions with and / or"
                className={RULE_CONJUNCTION_CLASS}
              >
                <option value="AND">and</option>
                <option value="OR">or</option>
              </select>
            ) : (
              <span className="block pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                {idx === 0 ? "Where" : matchType === "OR" ? "Or" : "And"}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {isGroup ? (
              <RuleGroupBox ruleFields={ruleFields} group={node} depth={depth} actions={actions} />
            ) : (
              <RuleLeafRow ruleFields={ruleFields} rule={node} actions={actions} />
            )}
          </div>

          <button
            type="button"
            onClick={() => actions.remove(node.id)}
            title={isGroup ? "Remove group" : "Remove condition"}
            className="mt-1.5 flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-acs-dark-border transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      );
    })}
  </div>
);

/**
 * @param {Array}  ruleFields  - field catalog for this grid (see filterTree.js)
 * @param {object} filters     - current { rules, matchType }
 * @param {func}   onApply     - (nextFilters) => void
 * @param {func}   onClose     - () => void
 */
export const AdvancedFilterModal = ({ ruleFields, filters, onApply, onClose }) => {
  const [rules, setRules] = useState(() =>
    (filters?.rules || []).map((r) => ({ ...r, id: r.id ?? makeRuleId() }))
  );
  const [matchType, setMatchType] = useState(() => ruleMatchType(filters));

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const actions = {
    changeField: (rule, nextFieldKey) => {
      const nextField = ruleFieldByKey(ruleFields, nextFieldKey);
      if (!nextField) return;
      const nextOp = nextField.ops.includes(rule.op) ? rule.op : nextField.ops[0];
      setRules((prev) =>
        updateRuleNode(prev, rule.id, (r) => ({
          ...r, field: nextFieldKey, op: nextOp, value: coerceValueForOp("", nextOp),
        }))
      );
    },
    changeOp: (rule, nextOp) =>
      setRules((prev) =>
        updateRuleNode(prev, rule.id, (r) => ({ ...r, op: nextOp, value: coerceValueForOp(r.value, nextOp) }))
      ),
    changeValue: (id, value) => setRules((prev) => updateRuleNode(prev, id, (r) => ({ ...r, value }))),
    remove: (id) => setRules((prev) => removeRuleNode(prev, id)),
    addRule: (groupId) =>
      setRules((prev) => appendRuleNode(prev, groupId, makeRule(ruleFields, ruleFields[0].key))),
    addGroup: (groupId, parentMatchType) =>
      setRules((prev) =>
        appendRuleNode(
          prev,
          groupId,
          makeRuleGroup(parentMatchType === "AND" ? "OR" : "AND", [
            makeRule(ruleFields, ruleFields[0].key),
            makeRule(ruleFields, ruleFields[1]?.key ?? ruleFields[0].key),
          ])
        )
      ),
    setMatchType: (groupId, value) => {
      if (!groupId) { setMatchType(value); return; }
      setRules((prev) => updateRuleNode(prev, groupId, (g) => ({ ...g, matchType: value })));
    },
  };

  const prunedRules = pruneRuleNodes(ruleFields, rules);
  const activeCount = countRuleNodes(prunedRules);
  const hasIncomplete = countRuleNodes(rules) > activeCount;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white dark:bg-acs-dark-surface border border-gray-200 dark:border-acs-dark-border rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-acs-dark-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-acs-dark-text">Advanced Filters</h2>
            {activeCount > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-600 text-white">
                {activeCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-acs-dark-border transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Show rows that match <span className="font-semibold">{matchType === "OR" ? "any" : "all"}</span> of the following.
            <span className="ml-1 text-gray-400 dark:text-gray-500">
              Use <span className="font-semibold">Add group</span> to nest mixed and/or logic.
            </span>
          </p>

          {rules.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-acs-dark-border px-4 py-6 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">No conditions yet — every row is shown.</p>
            </div>
          ) : (
            <RuleNodeList ruleFields={ruleFields} nodes={rules} matchType={matchType} groupId={null} depth={1} actions={actions} />
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <AddNodeButton onClick={() => actions.addRule(null)}>Add condition</AddNodeButton>
            <AddNodeButton onClick={() => actions.addGroup(null, matchType)}>Add group</AddNodeButton>
          </div>

          {hasIncomplete && (
            <p className="mt-3 text-xs text-custom-bg-blue dark:text-acs-secondary-purple">
              Conditions without a value are ignored, as are empty groups.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-acs-dark-border flex-shrink-0 bg-gray-50 dark:bg-acs-dark-surface">
          <button
            onClick={() => { setRules([]); setMatchType("AND"); }}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          >
            <RotateCcw size={14} /> Reset all
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-acs-dark-border text-gray-700 dark:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-acs-dark-border transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { onApply({ matchType, rules: prunedRules }); onClose(); }}
              className="px-5 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
            >
              Apply Filters
              {activeCount > 0 && (
                <span className="ml-1.5 bg-white/25 text-white text-xs rounded-full px-1.5 py-0.5">{activeCount}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AdvancedFilterModal;
